import { useCallback, useEffect, useRef, useState } from 'react'
import type { AudioClip, Composition } from '@shared/types'
import { api } from '../../../api'
import { useEditorStore } from '../../../store/editorStore'
import { useUiStore } from '../../../store/uiStore'
import { clipWindow, gainAt, sourceTimeAt } from '../../../lib/audioMath'
import { getTotalDurationSec } from '../../../lib/timelineMath'

/**
 * P10-1 担当C: 編集画面プレビューでの BGM/SFX 再生。
 *
 * Web Audio API は使わない — :3210（別オリジン）の音声を
 * `createMediaElementSource` に食わせると無音になる（P10_PHASE1.md §4-2）。
 * かわりに `composition.tracks.audio` の各 clip につき hidden `<audio>` を
 * 1 つ DOM に置き（PreviewPanel.tsx がこの hook の `audioClips`/`setAudioRef`/
 * `onLoadedMetadataFor` を使って実際に描画する）、`volume`/`currentTime`/
 * `play`/`pause` だけで同期する。usePreviewPlayback.ts の video 版と同じ設計
 * （毎 rAF tick で effect が回り、期待位置とズレたら合わせる）。
 *
 * BGM が尺より短い（`outSec:null` かつ source が不足）場合、実レンダー
 * （server/src/render/audio.ts）は acrossfade でループ結合するが、プレビュー
 * はそこまで再現しない — 1 回再生 + 不足時は `<audio loop>` 属性で近似する
 * （ループ境界のクロスフェードは無し。audioMath.ts の doc コメント参照）。
 */

/** 期待位置との許容ズレ（超えたら `currentTime` を合わせ直す）。P10_PHASE1.md §4-2: "video と同じ閾値" として明示された数値をそのまま使う（実際の usePreviewPlayback.ts の video 側は 0.2 — 差分は報告済み）。 */
const RESYNC_THRESHOLD_SEC = 0.2 // P10 司令塔: video 側 (usePreviewPlayback.ts) と同値に揃えた

/**
 * builtinId の `<audio>` に `loop` を付けるかどうかの近似判定: `outSec:null`
 * かつ、実際に分かっている source の長さ（`durationSec` あるいは `<audio>`
 * 自身がロードした `duration`）が、埋めるべき window の長さより短い時。
 * server 側の `buildAudioGraph` の shortfall 判定（`availableDur < neededDur`）
 * と同じ形だが、ratio 閾値（2倍未満はループしない）は再現しない — プレビュー
 * は「足りなければとにかく loop で埋める」という粗い近似に留める。
 */
function shouldLoop(clip: AudioClip, knownSourceDurationSec: number, window: { startSec: number; endSec: number }): boolean {
  if (clip.outSec != null) return false
  if (!Number.isFinite(knownSourceDurationSec)) return false
  const available = knownSourceDurationSec - clip.inSec
  const needed = window.endSec - window.startSec
  return available + 0.05 < needed
}

function resolveClipSrc(clip: AudioClip, assets: Record<string, { originalPath: string | null } | undefined>): string {
  if (clip.assetId) {
    return api.fileUrl(assets[clip.assetId]?.originalPath)
  }
  if (clip.builtinId) {
    return api.fileUrl(`builtin-audio/${clip.builtinId}`)
  }
  return ''
}

export function usePreviewAudio(composition: Composition | null) {
  const elementsRef = useRef<Map<string, HTMLAudioElement>>(new Map())
  const loadedSrcRef = useRef<Map<string, string>>(new Map())
  // Bumped by onLoadedMetadata so the main sync effect re-runs once a
  // builtinId clip's real duration becomes known (assets already carry
  // durationSec up-front; builtin clips have no such metadata client-side
  // until the <audio> itself loads it).
  const [metaTick, setMetaTick] = useState(0)

  const { currentSec, isPlaying } = useEditorStore((s) => s.playback)
  const assets = useEditorStore((s) => s.assets)
  const previewMuted = useUiStore((s) => s.previewMuted)

  const clips = composition?.tracks.audio ?? []
  const totalSec = composition ? getTotalDurationSec(composition) : 0
  const clipsKey = clips.map((c) => `${c.id}:${c.assetId ?? ''}:${c.builtinId ?? ''}`).join('|')

  // Per-clip-id ref callback cache: a `useCallback`-memoized OUTER function
  // still returns a brand-new INNER closure on every call, and PreviewPanel
  // re-renders on every rAF tick while playing — a `ref={setAudioRef(id)}`
  // whose function IDENTITY changes every render makes React invoke the old
  // ref with `null` then the new one with the element on EVERY render (React
  // ref semantics, not React-specific to audio), which cleared `el.src` right
  // back out moments after the sync effect set it (observed: `<audio src="">`
  // in practice). Caching one stable function per clip id — reused across
  // renders as long as the id is still in `clips` — avoids that churn; only
  // a REAL unmount (clip removed from the composition) reaches the `else`
  // branch below.
  const refCallbacksRef = useRef<Map<string, (el: HTMLAudioElement | null) => void>>(new Map())

  const setAudioRef = useCallback((clipId: string) => {
    const cached = refCallbacksRef.current.get(clipId)
    if (cached) return cached
    const fn = (el: HTMLAudioElement | null) => {
      if (el) {
        elementsRef.current.set(clipId, el)
      } else {
        const prev = elementsRef.current.get(clipId)
        if (prev) {
          prev.pause()
          prev.src = ''
        }
        elementsRef.current.delete(clipId)
        loadedSrcRef.current.delete(clipId)
        refCallbacksRef.current.delete(clipId)
      }
    }
    refCallbacksRef.current.set(clipId, fn)
    return fn
  }, [])

  const onLoadedMetadataFor = useCallback(
    (_clipId: string) => () => {
      setMetaTick((t) => t + 1)
    },
    [],
  )

  // Main sync effect: load src, seek, gain, play/pause, mute. Mirrors
  // usePreviewPlayback.ts's own single-effect design — runs on every
  // currentSec change (i.e. every rAF tick while playing, and on every
  // scrub-bar seek while paused).
  useEffect(() => {
    for (const clip of clips) {
      const el = elementsRef.current.get(clip.id)
      if (!el) continue

      const src = resolveClipSrc(clip, assets)
      if (src && loadedSrcRef.current.get(clip.id) !== src) {
        el.src = src
        el.currentTime = clip.inSec
        loadedSrcRef.current.set(clip.id, src)
      }

      el.muted = previewMuted

      // Prefer the <audio> element's own real (decoded) duration once known
      // — accurate for both assetId and builtinId clips — falling back to
      // the Asset's own durationSec (available immediately, before the
      // element has loaded anything) for assetId clips, and to "unknown yet"
      // (Infinity — clipWindow then just runs to the timeline end) otherwise.
      const domDuration = Number.isFinite(el.duration) && el.duration > 0 ? el.duration : undefined
      const assetDuration = clip.assetId ? assets[clip.assetId]?.durationSec : undefined
      const knownSourceDurationSec = domDuration ?? assetDuration ?? Number.POSITIVE_INFINITY

      const window = clipWindow(clip, knownSourceDurationSec, totalSec)
      el.loop = shouldLoop(clip, knownSourceDurationSec, window)

      const inWindow = currentSec >= window.startSec && currentSec < window.endSec
      if (!inWindow) {
        if (!el.paused) el.pause()
        continue
      }

      // reviewer 差し戻し C-1: `el.loop` の時だけ、既知の source 長から
      // `loopLenSec`（= source の実尺 - inSec）を渡す — 未知（Infinity）や
      // 0 以下なら渡さない（sourceTimeAt は単調増加のフォールバックのまま）。
      const available = Number.isFinite(knownSourceDurationSec) ? knownSourceDurationSec - clip.inSec : undefined
      const loopLenSec = el.loop && available != null && Number.isFinite(available) && available > 0 ? available : undefined
      const expected = sourceTimeAt(clip, currentSec, loopLenSec)
      if (Math.abs(el.currentTime - expected) > RESYNC_THRESHOLD_SEC) {
        el.currentTime = expected
      }
      el.volume = gainAt(clip, currentSec, window)

      if (isPlaying) {
        el.play().catch((err) => {
          // autoplay policy等での reject を握りつぶす（P10_PHASE1.md §4-2）。
          console.warn(`[usePreviewAudio] play() rejected for clip ${clip.id}:`, err)
        })
      } else {
        el.pause()
      }
    }
    // clipsKey captures id/assetId/builtinId changes; clips itself is a new
    // array reference every render so it can't be a dep directly without
    // firing on every unrelated re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clipsKey, currentSec, isPlaying, assets, previewMuted, totalSec, metaTick])

  return { audioClips: clips, setAudioRef, onLoadedMetadataFor }
}
